import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { 
  ArrowLeft, 
  Clock, 
  User, 
  FileText, 
  Eye, 
  Copy,
  CheckCircle,
  AlertCircle,
  Brain,
  MessageSquare,
  Filter,
  Calendar
} from 'lucide-react';

const ClaudeApiLogs = () => {
  const navigate = useNavigate();
  const { currentUser, familyUsers } = useFamilyContext();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filter, setFilter] = useState({
    endpoint: '',
    studentName: '',
    startDate: '',
    limit: 50
  });
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    loadLogs();
  }, [filter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('claude_api_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters
      if (filter.endpoint) {
        query = query.eq('endpoint', filter.endpoint);
      }
      if (filter.studentName) {
        query = query.eq('student_name', filter.studentName);
      }
      if (filter.limit) {
        query = query.limit(filter.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Failed to retrieve Claude API logs:', error);
        setLogs([]);
      } else {
        setLogs(data || []);
      }
    } catch (error) {
      console.error('Error loading Claude API logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getEndpointIcon = (endpoint) => {
    switch (endpoint) {
      case 'generate-conversational-insights':
        return <Brain className="w-4 h-4" />;
      case 'generate-dynamic-questions':
        return <MessageSquare className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = (success) => {
    return success ? 'text-emerald-600' : 'text-red-600';
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900">Loading API Logs...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/college')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to College Hub
        </button>
        
        <h1 className="text-3xl font-bold text-slate-900">Claude API Logs</h1>
        <p className="text-lg text-slate-600 mt-2">
          Review all API calls made to Claude for debugging and prompt analysis
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4" />
          <h3 className="font-semibold">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Endpoint</label>
            <select
              value={filter.endpoint}
              onChange={(e) => setFilter(prev => ({ ...prev, endpoint: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="">All Endpoints</option>
              <option value="generate-conversational-insights">Insights Generation</option>
              <option value="generate-dynamic-questions">Dynamic Questions</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Student</label>
            <select
              value={filter.studentName}
              onChange={(e) => setFilter(prev => ({ ...prev, studentName: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value="">All Students</option>
              {familyUsers?.filter(u => u.type !== 'parent').map(student => (
                <option key={student.id} value={student.displayName || student.name}>
                  {student.displayName || student.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Limit</label>
            <select
              value={filter.limit}
              onChange={(e) => setFilter(prev => ({ ...prev, limit: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              <option value={25}>25 entries</option>
              <option value={50}>50 entries</option>
              <option value={100}>100 entries</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Actions</label>
            <button
              onClick={loadLogs}
              className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold">API Call History ({logs.length} entries)</h3>
        </div>
        
        <div className="divide-y divide-slate-200">
          {logs.map((log) => (
            <div key={log.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getEndpointIcon(log.endpoint)}
                  <div>
                    <div className="font-medium text-slate-900">
                      {log.endpoint.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                    <div className="text-sm text-slate-600">
                      {log.student_name} • {log.phase} • {formatDate(log.created_at)}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <div className={`font-medium ${getStatusColor(log.success)}`}>
                      {log.success ? '✓ Success' : '✗ Failed'}
                    </div>
                    <div className="text-slate-600">
                      {formatDuration(log.duration_ms)}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg"
                  >
                    <Eye className="w-3 h-3" />
                    {selectedLog?.id === log.id ? 'Hide' : 'View'}
                  </button>
                </div>
              </div>
              
              {/* Expanded Details */}
              {selectedLog?.id === log.id && (
                <div className="mt-4 space-y-4">
                  {/* Prompt */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-900">Prompt Sent to Claude</h4>
                      <button
                        onClick={() => copyToClipboard(log.prompt, 'prompt')}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                      >
                        <Copy className="w-3 h-3" />
                        {copySuccess === 'prompt' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="bg-slate-50 border rounded-lg p-3">
                      <pre className="text-xs text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                        {log.prompt}
                      </pre>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {log.prompt_length?.toLocaleString()} characters
                    </div>
                  </div>
                  
                  {/* Response */}
                  {log.response_text && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900">Claude's Response</h4>
                        <button
                          onClick={() => copyToClipboard(log.response_text, 'response')}
                          className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                        >
                          <Copy className="w-3 h-3" />
                          {copySuccess === 'response' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <div className="bg-slate-50 border rounded-lg p-3">
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                          {log.response_text}
                        </pre>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {log.response_length?.toLocaleString()} characters
                      </div>
                    </div>
                  )}
                  
                  {/* Parsed Result */}
                  {log.parsed_result && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Parsed Result</h4>
                      <div className="bg-slate-50 border rounded-lg p-3">
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {JSON.stringify(log.parsed_result, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Error */}
                  {log.error_message && (
                    <div>
                      <h4 className="font-medium text-red-700 mb-2">Error</h4>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <pre className="text-xs text-red-700 whitespace-pre-wrap">
                          {log.error_message}
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4 text-xs text-slate-600">
                    <div>
                      <strong>Model:</strong> {log.request_body?.model || 'N/A'}
                    </div>
                    <div>
                      <strong>Max Tokens:</strong> {log.request_body?.max_tokens || 'N/A'}
                    </div>
                    <div>
                      <strong>Status Code:</strong> {log.response_status || 'N/A'}
                    </div>
                    <div>
                      <strong>Assessment ID:</strong> {log.assessment_id || 'N/A'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {logs.length === 0 && (
          <div className="p-8 text-center text-slate-600">
            <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No API logs found</h3>
            <p>No Claude API calls have been logged yet, or they don't match your current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaudeApiLogs;